import { useState, useEffect, useRef } from 'react'
import { useAuth, type UserMetadata } from '@/hooks/useAuth'
import { MinimalInput } from '@/components/ui/MinimalInput'
import { AlertCircle, CheckCircle2, Mail, Shield, Loader2 } from 'lucide-react'
import { COUNTRIES } from '@/lib/countries'

// Minimal autocomplete component for country selection
function MinimalAutocomplete({
  label,
  options,
  error,
  placeholder,
  value,
  name,
  onChange,
}: {
  label: string
  options: { value: string; label: string }[]
  error?: string
  placeholder?: string
  value: string
  name: string
  onChange: (e: { target: { name: string; value: string } }) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const selected = options.find(opt => opt.value === value)
    setInputValue(selected?.label || '')
  }, [value, options])

  const filteredOptions = inputValue
    ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
    : options

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        const selected = options.find(opt => opt.value === value)
        setInputValue(selected?.label || '')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value, options])

  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlighted = listRef.current.children[highlightedIndex] as HTMLElement
      if (highlighted) highlighted.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, isOpen])

  const handleSelect = (option: { value: string; label: string }) => {
    setInputValue(option.label)
    setIsOpen(false)
    onChange({ target: { name, value: option.value } })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setIsOpen(true)
    setHighlightedIndex(0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true)
        e.preventDefault()
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredOptions[highlightedIndex]) handleSelect(filteredOptions[highlightedIndex])
        break
      case 'Escape':
        setIsOpen(false)
        const selected = options.find(opt => opt.value === value)
        setInputValue(selected?.label || '')
        break
    }
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      <label className="block text-sm text-muted-foreground/80">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full bg-transparent border-b border-border/40 pb-2 text-foreground text-base
            placeholder:text-muted-foreground/40 focus:outline-none focus:border-foreground/60 transition-colors"
        />
        {isOpen && filteredOptions.length > 0 && (
          <ul
            ref={listRef}
            className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-auto bg-background border border-border/40 rounded shadow-lg"
          >
            {filteredOptions.map((option, index) => (
              <li
                key={option.value}
                onClick={() => handleSelect(option)}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                  index === highlightedIndex
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
        {isOpen && filteredOptions.length === 0 && inputValue && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 px-3 py-2 bg-background border border-border/40 rounded shadow-lg text-sm text-muted-foreground">
            No countries found
          </div>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

export function ProfileForm() {
  const { user, getUserMetadata, updateProfile } = useAuth()
  const currentMetadata = getUserMetadata()

  const [formData, setFormData] = useState<UserMetadata>({
    full_name: '',
    organization: '',
    role: '',
    country: '',
    signal_contact: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Load current metadata into form
  useEffect(() => {
    setFormData({
      full_name: currentMetadata.full_name || '',
      organization: currentMetadata.organization || '',
      role: currentMetadata.role || '',
      country: currentMetadata.country || '',
      signal_contact: currentMetadata.signal_contact || '',
    })
  }, [currentMetadata.full_name, currentMetadata.organization, currentMetadata.role, currentMetadata.country, currentMetadata.signal_contact])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string; value: string } }
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setSuccess(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setSaving(true)

    try {
      await updateProfile(formData)
      setSuccess(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update profile'
      )
    } finally {
      setSaving(false)
    }
  }

  const hasChanges =
    formData.full_name !== (currentMetadata.full_name || '') ||
    formData.organization !== (currentMetadata.organization || '') ||
    formData.role !== (currentMetadata.role || '') ||
    formData.country !== (currentMetadata.country || '') ||
    formData.signal_contact !== (currentMetadata.signal_contact || '')

  return (
    <div className="max-w-lg">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-xl font-medium text-foreground tracking-tight">Profile</h1>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Manage your account information
        </p>
      </div>

      {/* Email Info (Read-only) */}
      <div className="mb-8 p-4 bg-secondary/30 border border-border/20 rounded-lg">
        <div className="flex items-center gap-3">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="text-sm text-foreground">{user?.email}</p>
          </div>
          {user?.email_confirmed_at && (
            <div className="flex items-center gap-1.5 text-primary">
              <Shield className="h-3.5 w-3.5" />
              <span className="text-xs">Verified</span>
            </div>
          )}
        </div>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name and Org Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <MinimalInput
            label="Full name *"
            name="full_name"
            type="text"
            value={formData.full_name || ''}
            onChange={handleChange}
            placeholder="Your full name"
          />
          <MinimalInput
            label="Organization *"
            name="organization"
            type="text"
            value={formData.organization || ''}
            onChange={handleChange}
            placeholder="Your company or team"
          />
        </div>

        {/* Role and Country Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <MinimalInput
            label="Role *"
            name="role"
            type="text"
            value={formData.role || ''}
            onChange={handleChange}
            placeholder="Your role"
          />
          <MinimalAutocomplete
            label="Country *"
            name="country"
            value={formData.country || ''}
            onChange={handleChange}
            options={COUNTRIES}
            placeholder="Type to search..."
          />
        </div>

        {/* Signal Contact */}
        <MinimalInput
          label="Signal Contact (optional)"
          name="signal_contact"
          type="text"
          value={formData.signal_contact || ''}
          onChange={handleChange}
          placeholder="Phone number or username"
        />

        {/* Error Display */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="flex items-center gap-2 text-sm text-primary">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            <span>Profile updated successfully</span>
          </div>
        )}

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={!hasChanges || saving}
            className="w-full px-6 py-2.5 rounded-md text-sm font-medium transition-all
              bg-primary text-primary-foreground hover:bg-primary/90
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              'Save changes'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
